// Per-figure workflow:
//     1. Create figures/input/<name>.tex (standalone TikZ document).
//        Recommended: name it after the question's recordID.
//     2. From repo root: npm run compile-figure -- <name>
//        (or: npm run compile-figure -- --all)
//     3. Outputs land in figures/output/<name>.svg and <name>.alt.txt
//     4. Copy SVG to clipboard:
//          Get-Content figures\output\<name>.svg -Raw | Set-Clipboard
//     5. In DynamoDB (SATQuestions or AdaptiveDiagnosticQuestions),
//        edit the question item and add a Map attribute:
//           figure: {
//             type:    "svg"
//             svg:     <paste SVG string here>
//             altText: <optional plain-text description>
//           }
//     6. Refresh the question in the React app to verify.

import React, { useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import './SvgFigure.css';

const SANITIZE_CONFIG = {
  USE_PROFILES: { svg: true, svgFilters: true },
  ADD_TAGS: ['use'],
  ADD_ATTR: ['xlink:href', 'href'],
  FORBID_TAGS: ['script', 'foreignObject'],
  FORBID_ATTR: ['onload', 'onclick', 'onmouseover', 'onerror', 'onmouseenter', 'onmouseout'],
};

const ID_PREFIX = 'bb-';

/*
 * Figures always FIT their container — never a sideways scroll (Sandy
 * 2026-08-14: English graph questions were clipping in the half-width pane
 * and had to be scrolled; the whole chart must be visible at once). The SVG
 * carries intrinsic width/height, so max-w-full + h-auto scales it down
 * proportionally to whatever slot it is given. Tradeoff, accepted: in a very
 * narrow slot the labels shrink with the chart (the old min-w-[480px] floor
 * kept labels legible by scrolling instead — that floor is exactly what cut
 * charts off, so it is gone).
 */
const SIZE_CLASSES = {
  default: 'w-full max-w-lg [&>svg]:max-h-[400px]',
  large: 'w-full max-w-2xl [&>svg]:max-h-[600px]',
  compact: 'w-[200px] [&>svg]:max-h-[160px] pt-1 pb-1',
  // A coordinate-plane graph used as an answer choice ("which graph shows..."): at the compact 160px cap its tick
  // labels shrink to under 8px, so plane figures (data-fig="plane", drawn by the curriculum figure builder) get a
  // larger slot. Choice tables keep `compact`.
  // fluid (w-full, capped at 260px): a fixed 260px width forced a sideways scroll at 375px wide
  compactGraph: 'w-full max-w-[260px] [&>svg]:max-h-[240px] pt-1 pb-1',
};

const SvgFigure = ({
  svg,
  altText,
  className = '',
  interactive = true,
  selectionMode = 'multi',
  onElementClick,
  size = 'default',
}) => {
  const wrapperRef = useRef(null);
  const sanitized = useMemo(() => {
    if (typeof svg !== 'string' || svg.length === 0) return '';
    const clean = DOMPurify.sanitize(svg, SANITIZE_CONFIG);
    // Two figure-clipping causes, both fixed here (Sandy 2026-08-14: quant
    // tables/graphs cut off at the right edge):
    //  1. No viewBox + fixed width/height: CSS caps the width but the drawing
    //     keeps its native size and the right side falls off. Derive the
    //     viewBox from the declared size so max-w-full scales the drawing.
    //  2. Content drawn exactly on the viewBox boundary (TikZ exports put the
    //     table's outer border at x = width): a stroke on the edge loses half
    //     its width to clipping and vanishes when scaled. Pad the viewBox by
    //     2 units on every side (~0.4% at typical sizes, invisible).
    return clean.replace(/<svg\b[^>]*>/i, (tag) => {
      const vb = tag.match(/viewBox\s*=\s*"\s*([-\d.]+)[ ,]+([-\d.]+)[ ,]+([\d.]+)[ ,]+([\d.]+)\s*"/i);
      if (vb) {
        const [x, y, w, h] = [vb[1], vb[2], vb[3], vb[4]].map(Number);
        return tag.replace(vb[0], `viewBox="${x - 2} ${y - 2} ${w + 4} ${h + 4}"`);
      }
      const w = tag.match(/width\s*=\s*"([\d.]+)\s*(?:pt|px)?"/i);
      const h = tag.match(/height\s*=\s*"([\d.]+)\s*(?:pt|px)?"/i);
      if (!w || !h) return tag;
      return tag.replace(/>\s*$/, ` viewBox="-2 -2 ${Number(w[1]) + 4} ${Number(h[1]) + 4}">`);
    });
  }, [svg]);

  if (!sanitized) return null;
  const sizeKey = size === 'compact' && /<svg\b[^>]*\bdata-fig="plane"/.test(sanitized) ? 'compactGraph' : size;

  const handleClick = (e) => {
    if (!interactive) return;
    let node = e.target;
    while (node && node !== e.currentTarget) {
      if (node.id && node.id.startsWith(ID_PREFIX)) {
        const wasSelected = node.getAttribute('data-bb-selected') === 'true';
        const willBeSelected = !wasSelected;

        if (selectionMode === 'single' && willBeSelected && wrapperRef.current) {
          wrapperRef.current
            .querySelectorAll('[data-bb-selected="true"]')
            .forEach((el) => el.setAttribute('data-bb-selected', 'false'));
        }

        node.setAttribute('data-bb-selected', willBeSelected ? 'true' : 'false');

        if (onElementClick) {
          onElementClick({
            tag: node.tagName.toLowerCase(),
            id: node.id,
            element: node,
            selected: willBeSelected,
          });
        }
        return;
      }
      node = node.parentElement;
    }
  };

  return (
    <div
      ref={wrapperRef}
      role="img"
      aria-label={altText || 'Figure'}
      className={`${SIZE_CLASSES[sizeKey] || SIZE_CLASSES.default} mx-auto ${size === 'compact' ? '' : 'pt-2 pb-8'} [&>svg]:max-w-full [&>svg]:w-auto [&>svg]:h-auto [&>svg]:mx-auto [&>svg]:block ${interactive ? 'bb-svg-interactive' : ''} ${className}`}
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

export default SvgFigure;
